import React, { useEffect, useMemo, useState } from "react";
import {
  FILTER_BRANDS,
  GENDER_OPTIONS,
  PRICE_PRESETS,
  SIZE_GROUPS,
  SORT_OPTIONS,
  categoryIsAvailable,
  filterGenderOptions,
  groupAvailableSizes,
  groupSizesByMainCategory,
  isCategorySelected,
  isGenderSelected,
  isPresetActive,
  isSortSelected,
  sizesMatch,
} from "../helper/filterOptions";

const toDraft = (value) =>
  value === undefined || value === null || value === "" ? "" : String(value);

const parsePrice = (text) => {
  if (text === "" || text === undefined || text === null) return undefined;
  const parsed = parseInt(String(text), 10);
  return Number.isNaN(parsed) ? undefined : Math.max(0, parsed);
};

export function SortList({ activeSort, onChange }) {
  return (
    <ul className="filter-choice-list">
      {SORT_OPTIONS.map((option) => (
        <li key={option.value}>
          <button
            type="button"
            className={`filter-choice ${isSortSelected(activeSort, option.value) ? "is-selected" : ""}`}
            onClick={() => onChange(option.value === "newest" ? "newest" : option.value)}
          >
            {option.label}
          </button>
        </li>
      ))}
    </ul>
  );
}

export function GenderList({ activeGender, onChange, facetStatus, availableGenders }) {
  if (facetStatus === "loading") return <p className="filter-empty">Duke u ngarkuar</p>;
  const options = facetStatus === "ready" ? filterGenderOptions(availableGenders || []) : GENDER_OPTIONS;
  if (!options.length) return <p className="filter-empty">Nuk ka gjini</p>;

  return (
    <ul className="filter-choice-list">
      {options.map((option) => {
        const selected = isGenderSelected(activeGender, option.value);
        return (
          <li key={option.value}>
            <button
              type="button"
              className={`filter-choice ${selected ? "is-selected" : ""}`}
              onClick={() => onChange(selected ? undefined : option.value)}
            >
              {option.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function SizeGrid({ selected = [], onToggle, facetStatus, availableSizes, singleGroupLabel = null, sizesByCategory, catData, activeCategories }) {
  if (facetStatus === "loading") return <p className="filter-empty">Duke u ngarkuar</p>;
  const groups = facetStatus === "ready"
    ? groupSizesByMainCategory(sizesByCategory, catData, activeCategories, availableSizes || [])
      || groupAvailableSizes(availableSizes || [], { singleGroupLabel })
    : SIZE_GROUPS;
  if (!groups.length) return <p className="filter-empty">Nuk ka madhësi</p>;

  return (
    <div className="filter-size-groups">
      {groups.map((group) => (
        <div key={group.id} className="filter-size-group">
          <p className="filter-group-title">{group.label}</p>
          <div className="filter-size-grid">
            {group.sizes.map((size) => (
              <button
                type="button"
                key={`${group.id}-${size}`}
                className={`filter-size-chip ${selected.some((item) => sizesMatch(item, size)) ? "is-selected" : ""}`}
                onClick={() => onToggle(size)}
              >
                {size}
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function BrandList({ selected = [], onToggle, brands = FILTER_BRANDS }) {
  const [query, setQuery] = useState("");
  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return brands;
    return brands.filter((brand) => brand.toLowerCase().includes(needle));
  }, [brands, query]);

  return (
    <div className="filter-brand-list">
      <input
        type="search"
        className="filter-search"
        value={query}
        placeholder="Kërko brendin"
        onChange={(event) => setQuery(event.target.value)}
      />
      <ul className="filter-choice-list filter-choice-list--scroll">
        {visible.map((brand) => (
          <li key={brand}>
            <button
              type="button"
              className={`filter-choice ${selected.includes(brand) ? "is-selected" : ""}`}
              onClick={() => onToggle(brand)}
            >
              {brand}
            </button>
          </li>
        ))}
        {visible.length === 0 && <li className="filter-empty">Nuk u gjet brend</li>}
      </ul>
    </div>
  );
}

export function CategoryList({ catData = [], activeCategories = [], onToggle, facetStatus, availableCategoryIds }) {
  if (facetStatus === "loading") return <p className="filter-empty">Duke u ngarkuar</p>;
  const allowed = facetStatus === "ready" ? new Set((availableCategoryIds || []).map(String)) : null;
  const groups = catData
    .map((mainCat) => ({
      ...mainCat,
      children: (mainCat.groupCategory || []).filter((child) => categoryIsAvailable(child.linkedCategory, allowed)),
    }))
    .filter((mainCat) => mainCat.children.length > 0);

  if (!groups.length) return <p className="filter-empty">Nuk ka kategori</p>;

  return (
    <div className="filter-category-list">
      {groups.map((mainCat) => (
        <div key={mainCat._id || mainCat.name} className="filter-category-group">
          <p className="filter-group-title">{mainCat.name}</p>
          {mainCat.children.map((child) => {
            const selected = isCategorySelected(activeCategories, child.linkedCategory);
            return (
              <button
                type="button"
                key={child._id || child.name}
                className={`filter-choice ${selected ? "is-selected" : ""}`}
                onClick={() => onToggle(child.linkedCategory)}
              >
                {child.name}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

export function PricePanel({ min, max, onChange }) {
  const [minDraft, setMinDraft] = useState(() => toDraft(min));
  const [maxDraft, setMaxDraft] = useState(() => toDraft(max));

  useEffect(() => {
    setMinDraft(toDraft(min));
  }, [min]);

  useEffect(() => {
    setMaxDraft(toDraft(max));
  }, [max]);

  const commit = (minText = minDraft, maxText = maxDraft) => {
    let nextMin = parsePrice(minText);
    let nextMax = parsePrice(maxText);

    if (nextMin !== undefined && nextMax !== undefined && nextMin > nextMax) {
      const swapped = nextMin;
      nextMin = nextMax;
      nextMax = swapped;
      setMinDraft(toDraft(nextMin));
      setMaxDraft(toDraft(nextMax));
    }

    const currentMin = parsePrice(toDraft(min));
    const currentMax = parsePrice(toDraft(max));
    if (nextMin === currentMin && nextMax === currentMax) return;
    onChange(nextMin, nextMax);
  };

  return (
    <div className="filter-price-panel">
      <div className="filter-price-presets">
        {PRICE_PRESETS.map((preset) => (
          <button
            type="button"
            key={preset.id}
            className={`filter-preset ${isPresetActive(min, max, preset) ? "is-selected" : ""}`}
            onClick={() => onChange(preset.min, preset.max)}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className="price-range-inputs">
        <label className="price-range-field">
          <span>Nga</span>
          <input
            type="text"
            inputMode="numeric"
            value={minDraft}
            placeholder="0"
            onChange={(event) => setMinDraft(event.target.value.replace(/[^\d]/g, ""))}
            onBlur={() => commit()}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur();
            }}
          />
          <span>€</span>
        </label>
        <label className="price-range-field">
          <span>Deri</span>
          <input
            type="text"
            inputMode="numeric"
            value={maxDraft}
            placeholder="1500"
            onChange={(event) => setMaxDraft(event.target.value.replace(/[^\d]/g, ""))}
            onBlur={() => commit()}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.currentTarget.blur();
            }}
          />
          <span>€</span>
        </label>
      </div>
    </div>
  );
}

export function ActiveChips({ chips = [], onRemove, onClear }) {
  if (!chips.length) return null;

  return (
    <div className="filter-chips">
      {chips.map((chip) => (
        <button
          type="button"
          key={chip.id}
          className="filter-chip"
          onClick={() => onRemove(chip)}
        >
          <span>{chip.label}</span>
          <span aria-hidden="true">×</span>
        </button>
      ))}
      <button type="button" className="filter-clear" onClick={onClear}>
        Pastro
      </button>
    </div>
  );
}
